import { useAdvanceTargetingCampaignModalStore } from '@forwardflow/store/modal/AdvanceTargetingCampaignModal'
import { message } from 'antd'
import { useState, useEffect } from 'react'
import CampaignService from '@forwardflow/services/CampaignService'
import { CampaignData } from '@forwardflow/types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/features/campaign'
import { FilterEditor, IFilterCriteria } from '@forwardflow/features'
import { Modal } from '@mantine/core'
import { Button, TextInput } from '@forwardflow/ui-v2'

const SUPPORTED_FIELDS = [
   'source',
   'stage',
   'tags',
   'assigned_pond_id',
   'inquired_property',
]

const parseApiCriteria = (
   eligibleCriteria: any[]
): { supported: IFilterCriteria[]; preserved: any[] } => {
   const supported: IFilterCriteria[] = []
   const preserved: any[] = []

   eligibleCriteria.forEach((criteria: any) => {
      if (SUPPORTED_FIELDS.includes(criteria.field)) {
         supported.push({
            field: criteria.field,
            logic: 'AND',
            value: Array.isArray(criteria.value)
               ? criteria.value.map(String)
               : [String(criteria.value)],
            operator: criteria.operator || 'CONTAINS_ANY',
         })
      } else {
         preserved.push(criteria)
      }
   })

   return { supported, preserved }
}

const convertFiltersToApiCriteria = (filters: IFilterCriteria[]): any[] => {
   return filters
      .filter((f) => f.value.length > 0)
      .map((f) => ({
         field: f.field,
         operator: f.operator,
         value: f.value,
         logic: f.logic,
      }))
}

const convertFiltersToCampaignData = (
   filters: IFilterCriteria[],
   campaignName?: string
): CampaignData => {
   const campaignFilters = filters
      .filter((f) => f.value.length > 0)
      .map((f) => {
         switch (f.field) {
            case 'source':
               return {
                  type: 'source' as const,
                  sources: f.value.map(String),
               }
            case 'stage':
               return {
                  type: 'stages' as const,
                  stages: f.value.map(String),
               }
            case 'tags':
               return {
                  type: 'tags' as const,
                  tags: f.value.map(String),
                  tagFilterType: (f.operator || 'CONTAINS_ANY') as
                     | 'CONTAINS_ALL'
                     | 'CONTAINS_ANY',
               }
            case 'assigned_pond_id':
               return {
                  type: 'Ponds' as const,
                  assignedPonds: f.value.map(String),
               }
            case 'inquired_property':
               return {
                  type: 'inquired_property',
                  value: f.value.map(String),
               }
            default:
               return null
         }
      })
      .filter(Boolean) as any[]

   return {
      ...(campaignName ? { campaignName } : {}),
      filters: campaignFilters,
   }
}

export const AdvanceTargetingCampaignModal = () => {
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const isEditMode = searchParams.has('edit')
   const { title: leadSource } = useParams()

   const { data: activeLeadSourceData, mutate: mutateActiveLeadSource } =
      useGetActiveLeadSource(leadSource as string)
   const hasCampaignFiltersChanges =
      activeLeadSourceData?.draft?.changedSections?.includes(
         'campaign_filters'
      )
   const { data: campaignDetails, mutate: mutateActiveLeadSourceDetails } =
      useGetActiveLeadSourceDetails(leadSource as string)

   const {
      isAdvanceTargetingCampaignModalOpen,
      closeAdvanceTargetingCampaignModal,
   } = useAdvanceTargetingCampaignModalStore()

   const [campaignName, setCampaignName] = useState('')
   const [campaignNameError, setCampaignNameError] = useState('')
   const [isLoading, setIsLoading] = useState(false)
   const [filters, setFilters] = useState<IFilterCriteria[]>([
      { field: 'source', logic: 'AND', value: [], operator: 'CONTAINS_ANY' },
   ])
   const [preservedFilters, setPreservedFilters] = useState<any[]>([])

   const [estimatedLeads, setEstimatedLeads] = useState<number>(0)
   const [isLoadingEstimate, setIsLoadingEstimate] = useState(false)

   // Load existing data in edit mode
   useEffect(() => {
      if (!isEditMode) return

      const dataSource = hasCampaignFiltersChanges
         ? campaignDetails
         : activeLeadSourceData

      if (!dataSource) return

      setCampaignName(
         dataSource.campaignName ||
            searchParams.get('campaignName') ||
            ''
      )

      const eligibleCriteria =
         dataSource?.data?.campaignFilters?.eligibleCriteria
      if (Array.isArray(eligibleCriteria)) {
         const { supported, preserved } = parseApiCriteria(eligibleCriteria)
         setFilters(
            supported.length > 0
               ? supported
               : [
                    {
                       field: 'source',
                       logic: 'AND',
                       value: [],
                       operator: 'CONTAINS_ANY',
                    },
                 ]
         )
         setPreservedFilters(preserved)
      }
   }, [
      isEditMode,
      activeLeadSourceData,
      campaignDetails,
      hasCampaignFiltersChanges,
   ])

   // Estimated leads count
   useEffect(() => {
      const filtersWithValues = filters.filter((f) => f.value.length > 0)
      if (filtersWithValues.length === 0) {
         setEstimatedLeads(0)
         return
      }

      setIsLoadingEstimate(true)
      const campaignData = convertFiltersToCampaignData(filtersWithValues)
      const timeoutId = setTimeout(async () => {
         try {
            const response =
               await CampaignService.getEstimatedLeads(campaignData)
            setEstimatedLeads(response?.count || 0)
         } catch {
            setEstimatedLeads(0)
         } finally {
            setIsLoadingEstimate(false)
         }
      }, 500)

      return () => clearTimeout(timeoutId)
   }, [filters])

   const handleSave = async () => {
      if (!campaignName.trim()) {
         setCampaignNameError('Campaign name is required')
         return
      }

      setIsLoading(true)
      const validFilters = filters.filter((f) => f.value.length > 0)
      const eligibleCriteria = [
         ...convertFiltersToApiCriteria(validFilters),
         ...preservedFilters,
      ]
      const campaignFilters = {
         eligible_criteria: eligibleCriteria,
      }

      try {
         if (isEditMode && activeLeadSourceData?.id) {
            await CampaignService.editCustomActiveLeadSource(
               activeLeadSourceData.id,
               campaignFilters as any
            )
            const responseUpdatedLeadSource = (await CampaignService.updateCampaignName(
               activeLeadSourceData.id,
               campaignName
            )) as any

            message.success('Campaign updated successfully')

            if (responseUpdatedLeadSource) {
               const newSearchParams = new URLSearchParams(searchParams)
               newSearchParams.set(
                  'campaignName',
                  responseUpdatedLeadSource?.campaignName
               )
               navigate(
                  `${window.location.pathname}?${newSearchParams.toString()}`,
                  { replace: true }
               )
            }
         } else {
            const response = (await CampaignService.createCustomActiveLeadSource(
               campaignName,
               {
                  filters: [],
                  campaignName,
               },
               'new-lead'
            )) as any

            // After creating, update the filters directly
            if (response?.id && eligibleCriteria.length > 0) {
               await CampaignService.editCustomActiveLeadSource(
                  response.id,
                  campaignFilters as any
               )
            }

            if (response?.id) {
               navigate(
                  `/campaign/${encodeURIComponent(
                     response.id
                  )}/phone-number?campaignName=${campaignName}`
               )
            }
         }
      } catch {
         message.error(
            isEditMode
               ? 'Failed to update campaign'
               : 'Failed to create campaign'
         )
      } finally {
         closeAdvanceTargetingCampaignModal()
         setIsLoading(false)
         mutateActiveLeadSource()
         mutateActiveLeadSourceDetails()
      }
   }

   return (
      <Modal
         centered
         title=""
         size="xl"
         radius="lg"
         withCloseButton={false}
         classNames={{
            body: 'p-6',
         }}
         opened={isAdvanceTargetingCampaignModalOpen}
         onClose={closeAdvanceTargetingCampaignModal}
      >
         <div className="space-y-6">
            <div className="flex items-center justify-between">
               <p className="text-xl font-medium">
                  {isEditMode ? 'Edit Campaign Filters' : 'New Campaign'}
               </p>
               <p className="text-sm text-white/70">
                  Estimated Reach:{' '}
                  {isLoadingEstimate ? (
                     <span className="animate-pulse">Loading...</span>
                  ) : (
                     `${estimatedLeads.toLocaleString()} Leads`
                  )}
               </p>
            </div>

            <TextInput
               label="Campaign name"
               name="campaign_name"
               placeholder="Campaign Name"
               value={campaignName}
               onChange={(e) => {
                  setCampaignName(e.target.value)
                  if (campaignNameError) setCampaignNameError('')
               }}
               error={campaignNameError}
            />

            <div>
               <p className="mb-2 font-medium">Define Target Leads</p>
               <p className="mb-4 text-xs text-white/70">
                  Tell us who you want to reach – by source, stage, tags, or
                  ponds. We'll intelligently generate a tailored campaign.
               </p>
               <FilterEditor filters={filters} onChange={setFilters} />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
               <Button
                  variant="secondary"
                  className="w-44"
                  onClick={closeAdvanceTargetingCampaignModal}
               >
                  Cancel
               </Button>
               <Button
                  className="w-44"
                  onClick={handleSave}
                  loading={isLoading}
               >
                  {isEditMode ? 'Update' : 'Activate'}
               </Button>
            </div>
         </div>
      </Modal>
   )
}
